import {reservationTicketEmail} from '../../utils/reservation-ticket';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import {
  cilCalendar,
  cilCheck,
  cilCheckCircle,
  cilSave,
  cilWarning,
  cilXCircle,
} from '@coreui/icons';
import { AltchaComponent } from '../../components/altcha/altcha.component';
import {
  DefaultplusService,
  DefaultplusServicesService,
} from '../../services/defaultplus-services.service';

const MEETING_BASE_URL = 'https://meeting.sagel.app';

type SupportedLanguage = 'en' | 'tr' | 'es' | 'el' | 'de';

/**
 * Customer-facing DefaultPlus appointment edit page.
 *
 * Reached from the `/edit-appointment/:company/:id` link the dp confirmation
 * email already ships, so the route is unchanged. Rebuilt from the old generic
 * form onto the dp service-catalog model: the service is picked from the
 * tenant's public catalog (not free text), the end time follows the service's
 * durationMin, and every date/time/service change is validated against
 * `POST /availability/check` (mode=defaultplus) — the same slot-aware contract
 * as the booking form. Saves go through the existing guest update endpoint.
 */
@Component({
    selector: 'app-edit-appointment',
    templateUrl: './edit-appointment.component.html',
    styleUrls: ['./edit-appointment.component.scss'],
    standalone: false
})
export class EditAppointmentComponent implements OnInit {
  private ticketLink:string|null=null;
  @Input() ticketContext?: {companyName:string;reservationId:string;language?:string};
  icons = { cilCalendar, cilCheck, cilCheckCircle, cilSave, cilWarning, cilXCircle };

  companyName!: string;
  reservationId!: string;
  private userId!: string;

  // Form fields
  name = '';
  date = '';
  time = '';
  // The CoreUI date picker binds a Date; `date`/`time` stay the API strings.
  dateModel: Date | null = null;
  minDate = new Date();
  email = '';
  phone = '';
  notes = '';
  meetingUrl = '';

  // Service catalog
  catalog: DefaultplusService[] = [];
  selectedService: DefaultplusService | null = null;
  private rawServiceName = '';

  // The slot the appointment already holds — an unchanged edit must never be
  // flagged as a conflict with itself (the availability endpoint has no
  // self-exclusion), so we skip the check while date/time/service are original.
  private originalDate = '';
  private originalTime = '';
  private originalServiceName = '';

  // Time slots (15-min, availability-aware) — mirror of the booking form. The
  // appointment's own current slot is always offered even when self-occupied.
  readonly slotStepMin = 15;
  slotsLoading = false;
  slotsUnavailable = false;
  daySlots: string[] = [];
  private slotReq$ = new Subject<void>();

  // Closed days (disabled in the date picker).
  private readonly weekdayKeys = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];
  private closedWeekdays: Record<string, boolean> = {};
  private closedDateKeys = new Set<string>();
  fullyClosedDates: Date[] = [];
  dateFilter: (date: Date) => boolean = () => true;

  // Meeting link auto-update
  private meetingUrlIsGenerated = false;
  private readonly timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  private lang: SupportedLanguage = 'en';

  @ViewChild(AltchaComponent) altcha?: AltchaComponent;

  // Stepper (2 stages: service & time → your details).
  currentStep = 1;
  readonly totalSteps = 2;
  stepError: string | null = null;
  contactValidationAttempted = false;

  // Captcha — the public update endpoint requires an Altcha token.
  captchaToken: string | null = null;
  captchaReady = false;

  isLoading = false;
  isSaving = false;
  success: boolean | null = null;
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
    private servicesApi: DefaultplusServicesService,
  ) {
    const supported: SupportedLanguage[] = ['en', 'tr', 'es', 'el', 'de'];
    this.translate.addLangs(supported);
    this.translate.setDefaultLang('en');
    const browserLang = navigator.language.split('-')[0] as SupportedLanguage;
    this.lang = supported.includes(browserLang) ? browserLang : 'en';
    this.translate.use(this.lang);
  }

  ngOnInit(): void {
    this.slotReq$.pipe(debounceTime(300)).subscribe(() => this.loadDaySlots());

    if (this.ticketContext) {
      if(this.ticketContext.language)this.translate.use(this.ticketContext.language);
      this.companyName=this.ticketContext.companyName;
      this.reservationId=this.ticketContext.reservationId;
      this.resolveTenant();
      return;
    }

    this.route.paramMap.subscribe((params) => {
      const company = params.get('companyName');
      const id = params.get('reservationId');
      if (company && id) {
        this.companyName = decodeURIComponent(company);
        this.reservationId = id;
        this.resolveTenant();
      } else {
        this.success = false;
        this.errorMessage = this.translate.instant('EDIT_APPOINTMENT.INVALID_LINK');
      }
    });
  }

  private resolveTenant(): void {
    this.isLoading = true;
    this.http
      .get<any>(`${environment.baseUrl}/users/get-id-by-company-name`, {
        params: { company_name: this.companyName },
      })
      .subscribe({
        next: (res) => {
          this.userId = res.user_id;
          this.loadClosures();
          // Catalog first so the reservation's service resolves to a catalog
          // entry (and its duration) when the row loads.
          this.loadCatalog(() => this.fetchReservation());
        },
        error: () => {
          this.isLoading = false;
          this.success = false;
          this.errorMessage = this.translate.instant('EDIT_APPOINTMENT.NOT_FOUND');
        },
      });
  }

  private loadCatalog(done: () => void): void {
    this.servicesApi.getPublicServices(this.userId).subscribe({
      next: (services) => {
        this.catalog = services;
        done();
      },
      error: () => {
        this.catalog = [];
        done();
      },
    });
  }

  private fetchReservation(): void {
    this.http
      .get<any>(`${environment.baseUrl}/get-reservations?userId=${this.userId}`)
      .subscribe({
        next: (data) => {
          const rows: any[] = Array.isArray(data?.reservations) ? data.reservations : [];
          const r = rows.find((x) => String(x.reservation_id) === String(this.reservationId));
          if (r) {
            const info = this.parseInfo(r.additional_info);
            this.name = info['name'] || r.name || '';
            // Normalize whatever the row carries (ISO timestamp, dd/MM/yyyy, …)
            // to yyyy-MM-dd so the picker and availability API get a clean date.
            this.date = this.toDateKey(r.date || info['date'] || '');
            this.time = (r.time || info['time'] || '').slice(0, 5);
            this.email = info['customer_email'] || r.customer_email || r.source_id || '';
            this.phone = info['customer_phone'] || r.customer_phone || '';
            this.notes = info['notes'] || r.notes || '';
            this.rawServiceName = info['service'] || r.service || '';
            this.meetingUrl = info['meeting_url'] || r.meeting_url || '';
            this.meetingUrlIsGenerated = this.meetingUrl.startsWith(MEETING_BASE_URL);
            this.originalDate = this.date;
            this.originalTime = this.time;
            this.originalServiceName = this.rawServiceName;
            this.dateModel = this.date ? new Date(this.date + 'T00:00:00') : null;
            this.matchService();
            // Keep the original time selected; refreshSlots offers it even
            // though the appointment occupies it.
            this.refreshSlots();
          }
          this.isLoading = false;
        },
        error: () => { this.isLoading = false; },
      });
  }

  private matchService(): void {
    if (!this.rawServiceName) return;
    const found = this.catalog.find(
      (s) => s.name.trim().toLowerCase() === this.rawServiceName.trim().toLowerCase(),
    );
    this.selectedService = found || null;
  }

  onServiceChange(): void {
    this.time = '';
    if (this.meetingUrlIsGenerated) this.meetingUrl = this.buildGeneratedMeetingUrl();
    this.refreshSlots();
  }

  selectSlot(slot: string): void {
    this.time = slot;
    this.stepError = null;
    if (this.meetingUrlIsGenerated) this.meetingUrl = this.buildGeneratedMeetingUrl();
  }

  // ── Stepper control ───────────────────────────────────────────────────────
  nextStep(): void {
    this.stepError = null;
    if (!this.selectedService) {
      this.stepError = this.translate.instant('EDIT_APPOINTMENT.SERVICE_PLACEHOLDER');
      return;
    }
    if (!this.time) {
      this.stepError = this.translate.instant('EDIT_APPOINTMENT.SLOT_REQUIRED');
      return;
    }
    this.currentStep = 2;
  }

  prevStep(): void {
    this.stepError = null;
    this.currentStep = 1;
  }

  /** End time = start + selected service duration; falls back to blank. */
  get endTime(): string {
    if (!this.selectedService) return '';
    const [h, m] = (this.time || '00:00').split(':').map(Number);
    const total = h * 60 + m + Number(this.selectedService.durationMin || 0);
    const endH = Math.floor(total / 60) % 24;
    const endM = total % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  }

  onDateChange(value: Date | string): void {
    const key = this.toDateKey(value);
    if (key) this.date = key;
    this.time = '';
    if (this.meetingUrlIsGenerated) this.meetingUrl = this.buildGeneratedMeetingUrl();
    this.refreshSlots();
  }

  private toDateKey(value: Date | string): string {
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return '';
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    const raw = String(value || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const m = raw.match(/^(\d{2})[./](\d{2})[./](\d{4})$/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? '' : this.toDateKey(parsed);
  }

  // ── Time slots (availability-aware, 15-min) ──────────────────────────────
  private refreshSlots(): void {
    this.daySlots = [];
    this.slotsUnavailable = false;
    if (this.selectedService && this.date) {
      this.slotsLoading = true;
      this.slotReq$.next();
    }
  }

  private loadDaySlots(): void {
    if (!this.selectedService || !this.date || !this.userId) {
      this.slotsLoading = false;
      return;
    }
    this.http
      .post<any>(`${environment.baseUrl}/availability/check`, {
        user_id: this.userId,
        date: this.date,
        time: '12:00',
        service: this.selectedService.name,
        availability_mode: 'defaultplus',
      })
      .subscribe({
        next: (res) => {
          this.slotsLoading = false;
          const gaps = Array.isArray(res?.freeGaps) ? res.freeGaps : [];
          let slots: string[];
          if (gaps.length) {
            slots = this.slotsFromGaps(gaps);
          } else if (res?.available === false) {
            slots = [];
          } else {
            // Enforcement disabled or failed open: default grid, server decides.
            slots = this.fallbackSlots();
          }
          slots = this.withOriginalSlot(slots);
          this.daySlots = slots;
          this.slotsUnavailable = slots.length === 0;
        },
        error: () => {
          this.slotsLoading = false;
          this.daySlots = this.withOriginalSlot(this.fallbackSlots());
          this.slotsUnavailable = this.daySlots.length === 0;
        },
      });
  }

  /** Keep the appointment's own time selectable on its original day/service. */
  private withOriginalSlot(slots: string[]): string[] {
    if (
      this.originalTime &&
      this.date === this.originalDate &&
      this.selectedService?.name === this.originalServiceName &&
      !slots.includes(this.originalTime)
    ) {
      return [...slots, this.originalTime].sort();
    }
    return slots;
  }

  private slotsFromGaps(gaps: any[]): string[] {
    const duration = Number(this.selectedService?.durationMin || 0);
    const step = this.slotStepMin;
    const floor = this.minStartForDate();
    const out: string[] = [];
    for (const g of gaps) {
      const range = this.parseGap(g);
      if (!range) continue;
      let t = Math.ceil(range.startMin / step) * step;
      if (t < floor) t = Math.ceil(floor / step) * step;
      while (t + duration <= range.endMin) {
        out.push(this.minutesToHHMM(t));
        t += step;
      }
    }
    return Array.from(new Set(out)).sort();
  }

  /** freeGaps arrive as "HH:MM–HH:MM" strings; tolerate {startMin,endMin} too. */
  private parseGap(g: any): { startMin: number; endMin: number } | null {
    if (g && Number.isFinite(g.startMin) && Number.isFinite(g.endMin)) {
      return { startMin: g.startMin, endMin: g.endMin };
    }
    const m = String(g || '').match(/(\d{1,2}):(\d{2})\s*[–—-]\s*(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return { startMin: +m[1] * 60 + +m[2], endMin: +m[3] * 60 + +m[4] };
  }

  private fallbackSlots(): string[] {
    const duration = Number(this.selectedService?.durationMin || 0);
    const step = this.slotStepMin;
    const floor = Math.max(8 * 60, this.minStartForDate());
    const end = 21 * 60;
    const out: string[] = [];
    for (let t = Math.ceil(floor / step) * step; t + duration <= end; t += step) {
      out.push(this.minutesToHHMM(t));
    }
    return out;
  }

  private minStartForDate(): number {
    const now = new Date();
    const todayKey = this.toDateKey(now);
    if (this.date !== todayKey) return 0;
    return now.getHours() * 60 + now.getMinutes();
  }

  private minutesToHHMM(mins: number): string {
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // ── Closed days ──────────────────────────────────────────────────────────
  private loadClosures(): void {
    this.http
      .get<any>(`${environment.baseUrl}/closed-days/get?user_id=${this.userId}`)
      .subscribe({
        next: (res) => { this.closedWeekdays = res?.data || {}; this.rebuildDateFilter(); },
        error: () => { this.closedWeekdays = {}; this.rebuildDateFilter(); },
      });
    this.http
      .get<any>(`${environment.baseUrl}/closed-dates/get/${this.userId}`)
      .subscribe({
        next: (res) => {
          const rows: any[] = Array.isArray(res?.data) ? res.data : [];
          this.closedDateKeys = new Set(
            rows.map((r) => this.normalizeDateKey(r?.closed_date)).filter(Boolean),
          );
          this.fullyClosedDates = [...this.closedDateKeys].map((k) => new Date(k + 'T00:00:00'));
          this.rebuildDateFilter();
        },
        error: () => {
          this.closedDateKeys = new Set();
          this.fullyClosedDates = [];
          this.rebuildDateFilter();
        },
      });
  }

  private rebuildDateFilter(): void {
    this.dateFilter = (date: Date) => {
      if (!date) return true;
      if (this.closedWeekdays[this.weekdayKeys[date.getDay()]]) return false;
      return !this.closedDateKeys.has(this.toDateKey(date));
    };
  }

  private normalizeDateKey(raw: any): string {
    const s = String(raw || '').trim();
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(s)) {
      const [d, m, y] = s.split(/[./]/);
      return `${y}-${m}-${d}`;
    }
    return s.slice(0, 10);
  }

  private buildGeneratedMeetingUrl(): string {
    if (!this.date || !this.time || !this.companyName) return '';
    const companySlug = this.slugify(this.companyName);
    const timePart = (this.time || '00:00').replace(':', '-');
    const timezonePart = this.timezone.replace(/\//g, '_').replace(/\s+/g, '');
    const titleSlug = this.slugify((this.selectedService?.name || 'appointment') + '-' + (this.name || 'meeting'));
    const code = `${titleSlug}-${this.date}_${timePart}-${timezonePart}-${this.lang}`;
    return `${MEETING_BASE_URL}/${companySlug}/${encodeURIComponent(code)}`;
  }

  private slugify(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  save(): void {
    if (this.isSaving) return;
    this.contactValidationAttempted = true;
    if (!this.isEmailValid() || !this.isPhoneValid()) {
      this.errorMessage = this.translate.instant(
        'EDIT_RESERVATION.VALIDATION_STEP_THREE'
      );
      return;
    }
    if (!this.time) {
      this.errorMessage = this.translate.instant('EDIT_APPOINTMENT.SLOT_REQUIRED');
      return;
    }
    if (!this.captchaReady) {
      this.errorMessage = this.translate.instant('CAPTCHA.LOADING');
      return;
    }
    this.errorMessage = '';
    this.isSaving = true;

    const payload = {
      name: this.name,
      date: this.date,
      time: this.time.length === 5 ? `${this.time}:00` : this.time,
      customer_email: this.email,
      customer_phone: this.phone,
      service: this.selectedService?.name || this.rawServiceName,
      notes: this.notes,
      meeting_url: this.meetingUrl,
      captcha_token: this.captchaToken,
    };

    const url = `${environment.baseUrl}/reservations/public/users/${this.userId}/reservations/${this.reservationId}/update`;

    this.http.post<any>(url, payload).subscribe({
      next: (res) => {this.ticketLink=res?.ticket_link||null;this.onSaveSuccess(payload);},
      error: () => this.onSaveError(),
    });
  }

  isEmailValid(): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(this.email || '').trim());
  }

  isPhoneValid(): boolean {
    return /^[0-9+()\-\s]{7,20}$/.test(String(this.phone || '').trim());
  }

  private onSaveSuccess(payload: any): void {
    this.isSaving = false;
    this.success = true;
    if (this.email) {
      this.sendUpdateEmail(payload);
    }
  }

  private onSaveError(): void {
    this.isSaving = false;
    this.success = false;
    // A used/expired Altcha challenge can't be replayed — reset for a retry.
    this.altcha?.reset();
    this.captchaReady = false;
    this.errorMessage = this.translate.instant('EDIT_APPOINTMENT.ERROR_TEXT');
  }

  /**
   * Update confirmation email — same visual language as the dp booking
   * confirmation (APPOINTMENT_BOOKING.MAIL.*), only the heading/subject say
   * "Updated". Restaurant emails are a separate component, untouched.
   */
  private sendUpdateEmail(payload: any): void {
    const t = (key: string, params?: any) => this.translate.instant(key, params);
    const encodedCompany = encodeURIComponent(this.companyName);
    const cancelLink = `${environment.baseUrl}/#/cancel/${encodedCompany}/${this.reservationId}`;
    const editLink = `${environment.baseUrl}/#/edit/${encodedCompany}/${this.reservationId}`;
    const meetingUrl = payload.meeting_url || '';

    const locales: Record<SupportedLanguage, string> = {
      en: 'en-GB', tr: 'tr-TR', es: 'es-ES', el: 'el-GR', de: 'de-DE',
    };
    const locale = locales[this.lang] ?? 'en-GB';
    const dateObj = new Date(payload.date + 'T00:00:00');
    const longDate = dateObj.toLocaleDateString(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const displayTime = payload.time.slice(0, 5);
    const displayRange = this.endTime ? `${displayTime} - ${this.endTime}` : displayTime;
    const timezone = this.timezone;

    const phoneRow = payload.customer_phone
      ? `<tr><td style="color:#888;padding:4px 0;vertical-align:top;">${t('APPOINTMENT_BOOKING.MAIL.PHONE')}</td><td style="padding:4px 0;">${payload.customer_phone}</td></tr>`
      : '';
    const notesRow = payload.notes
      ? `<tr><td style="color:#888;padding:4px 0;vertical-align:top;">${t('APPOINTMENT_BOOKING.MAIL.NOTES')}</td><td style="padding:4px 0;">${payload.notes}</td></tr>`
      : '';
    const meetingRow = meetingUrl
      ? `<tr><td style="color:#888;padding:4px 0;vertical-align:top;">${t('APPOINTMENT_BOOKING.MAIL.MEETING')}</td><td style="padding:4px 0;"><a href="${meetingUrl}" style="color:#1a3d8f;word-break:break-all;">${meetingUrl}</a></td></tr>`
      : '';

    const subject = t('EDIT_APPOINTMENT.MAIL_SUBJECT', {
      service: payload.service,
      company: this.companyName,
    });
    const heading = t('EDIT_APPOINTMENT.MAIL_HEADING');

    const html = `
<div style="font-family:Arial,sans-serif;background:#f5f5f5;padding:24px 16px;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0"
    style="max-width:560px;margin:0 auto;background:#ffffff;border:1px solid #e0e0e0;border-radius:4px;">
    <tr>
      <td style="padding:24px 32px;border-bottom:2px solid #1a3d8f;">
        <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">${this.companyName}</div>
        <div style="font-size:20px;font-weight:600;color:#1a1a1a;">${payload.service} — ${heading}</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <div style="font-size:13px;color:#888;margin-bottom:4px;">${longDate}</div>
        <div style="font-size:22px;font-weight:600;color:#1a1a1a;">${displayRange}</div>
        <div style="font-size:12px;color:#888;margin-top:4px;">${timezone}</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <table width="100%" cellspacing="0" cellpadding="0" style="font-size:13px;color:#333;line-height:1.6;">
          <tr>
            <td style="width:120px;color:#888;padding:4px 0;vertical-align:top;">${t('APPOINTMENT_BOOKING.MAIL.CONFIRMATION_ID')}</td>
            <td style="padding:4px 0;font-weight:700;color:#1a1a1a;letter-spacing:0.04em;">${this.reservationId}</td>
          </tr>
          <tr>
            <td style="color:#888;padding:4px 0;vertical-align:top;">${t('APPOINTMENT_BOOKING.MAIL.NAME')}</td>
            <td style="padding:4px 0;">${payload.name || ''}</td>
          </tr>
          <tr>
            <td style="color:#888;padding:4px 0;vertical-align:top;">${t('APPOINTMENT_BOOKING.MAIL.SERVICE')}</td>
            <td style="padding:4px 0;">${payload.service || ''}</td>
          </tr>
          ${phoneRow}
          ${notesRow}
          ${meetingRow}
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;text-align:center;">
        ${meetingUrl ? `<a href="${meetingUrl}" style="display:inline-block;padding:10px 22px;background:#1a3d8f;color:#fff;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;margin:0 6px 8px;">${t('APPOINTMENT_BOOKING.MAIL.JOIN')}</a>` : ''}
        <a href="${editLink}" style="display:inline-block;padding:10px 22px;background:#f0f0f0;color:#1a1a1a;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;margin:0 6px 8px;">${t('APPOINTMENT_BOOKING.MAIL.UPDATE')}</a>
        <a href="${cancelLink}" style="display:inline-block;padding:10px 22px;background:#f0f0f0;color:#c0392b;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;margin:0 6px 8px;">${t('APPOINTMENT_BOOKING.MAIL.CANCEL')}</a>
      </td>
    </tr>
    <tr>
      <td style="padding:16px 32px;text-align:right;font-size:11px;color:#aaa;">
        Powered by Sagel &nbsp;·&nbsp; ID: ${this.reservationId}
      </td>
    </tr>
  </table>
</div>`;

    this.http.post(`${environment.baseUrl}/email_notifications/info`, {
      header: subject,
      content: reservationTicketEmail(html,this.ticketLink,this.translate.instant('RESERVATION_TICKET.VIEW_MANAGE')),
      to_addr: this.email,
    }).subscribe();
  }

  private parseInfo(value: any): Record<string, any> {
    if (!value) return {};
    if (typeof value === 'string') {
      try { return JSON.parse(value); } catch { return {}; }
    }
    return value;
  }
}
